import {
  joinSlackChannels,
  slackPickerChannelsQueryKey,
  slackPickerChannelsQueryOptions,
} from "@/shared/slackChannelQueries.ts";
import { isPreselectedSlackChannel, type SlackPickerChannel } from "@/types/slackChannels.ts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";

/**
 * The channel list, the user's checks, and the join, shared by web's
 * post-connect modal and in-chat card, and by native's.
 *
 * Checks start from the server's list (public channels okthx isn't in) and are
 * the user's from then on. Unlike the other pickers nothing saves per row: a
 * pre-checked channel the user never touched still has to be joined, so the
 * join is one explicit submit.
 */
export function useSlackChannelPicker(teamId: string) {
  const queryClient = useQueryClient();
  const query = useQuery(slackPickerChannelsQueryOptions(teamId));
  const channels = useMemo(() => query.data?.channels ?? [], [query.data]);
  // `undefined` until the user changes something, so the defaults follow the
  // list as it loads rather than freezing on an empty first render.
  const [picked, setPicked] = useState<ReadonlySet<string> | undefined>(undefined);
  // Only channels that can still be joined count, so a channel okthx joined
  // since the pick was made drops out of the count and the request.
  const joinable = useMemo(() => channels.filter((c) => !c.okthxIsMember), [channels]);
  const selected = useMemo(() => {
    if (!picked) return new Set(joinable.filter(isPreselectedSlackChannel).map((c) => c.id));
    return new Set(joinable.filter((c) => picked.has(c.id)).map((c) => c.id));
  }, [picked, joinable]);

  const toggle = (channelId: string) =>
    setPicked((prev) => {
      const next = new Set(prev ?? selected);
      if (next.has(channelId)) next.delete(channelId);
      else next.add(channelId);
      return next;
    });

  /** Check every channel okthx can still join, private and external included, or none. */
  const setAll = (checked: boolean) =>
    setPicked(new Set(checked ? joinable.map((c) => c.id) : []));

  const join = useMutation({
    mutationFn: () => joinSlackChannels({ teamId, channelIds: [...selected] }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: slackPickerChannelsQueryKey(teamId) }),
  });

  return {
    query,
    publicChannels: channels.filter((c) => !c.isPrivate),
    privateChannels: channels.filter((c) => c.isPrivate),
    isSelected: (channel: SlackPickerChannel) => channel.okthxIsMember || selected.has(channel.id),
    toggle,
    setAll,
    /** Every joinable channel is checked; false when there is nothing to join. */
    allSelected: joinable.length > 0 && selected.size === joinable.length,
    selectedCount: selected.size,
    join,
  };
}
